// src/pages/RelatorioEncapsulacaoDetalhado.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  TextField, Button, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TablePagination, Autocomplete
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  Medication as CapsuleIcon,
  Factory as FactoryIcon,
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  List as ListIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

const RelatorioEncapsulacaoDetalhado = () => {
  const { api } = useAuth();
  const location = useLocation();
  
  // Suprimir erro do proxy (extensões do browser)
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0]?.toString().includes('disconnected port object')) {
        return; // Ignorar erro do proxy
      }
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dados, setDados] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    funcionario: '',
    tipoQuantidade: '',
    quantidade: ''
  });
  const [funcionarios, setFuncionarios] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [temProximaPagina, setTemProximaPagina] = useState(false);

  // Tipos de quantidade disponíveis
  const tiposQuantidade = [
    { value: '1', label: 'Cápsulas' },
    { value: '2', label: 'Sachês' },
    { value: '3', label: 'Xaropes' },
    { value: '4', label: 'Dermatológicos' },
    { value: '5', label: 'Ordem de Produção' },
    { value: '6', label: 'Ortopédicos/Farmacêuticos' }
  ];

  // Carregar funcionários
  const carregarFuncionarios = useCallback(async () => {
    try {
      console.log('🔄 Carregando funcionários...');
      const response = await api.listarFuncionarios();
      console.log('📡 Resposta API funcionários:', response);
      
      if (response.success) {
        setFuncionarios(response.dados || []);
        console.log('👥 Funcionários carregados:', response.dados?.length || 0);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar funcionários:', error);
    }
  }, [api]);

  // Carregar dados detalhados
  const carregarDados = useCallback(async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período (data início e fim)');
      return;
    }

    console.log('🔍 Filtros atuais:', filtros);

    try {
      setLoading(true);
      setError('');
      console.log('📊 Iniciando carregamento de dados detalhados...');

      const parametros = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        setor: 'Encapsulação', // Sempre Encapsulação
        limite: rowsPerPage,
        pagina: page + 1
      };

      if (filtros.funcionario && filtros.funcionario !== '') {
        parametros.funcionario = filtros.funcionario;
      }

      if (filtros.tipoQuantidade && filtros.tipoQuantidade !== '') {
        parametros.tipoQuantidade = filtros.tipoQuantidade;
      }

      if (filtros.quantidade && filtros.quantidade !== '') {
        parametros.quantidade = filtros.quantidade;
      }

      console.log('📦 Parâmetros sendo enviados:', parametros);
      const response = await api.relatorioVolumeProducao(parametros);
      console.log('📡 Resposta da API:', response);
      
      if (response.success) {
        const dados = response.dados || [];
        console.log('📊 Dados recebidos:', dados.length, 'registros');
        
        // Log dos primeiros dados para debug
        if (dados.length > 0) {
          console.log('🔍 Primeiro registro da API:', dados[0]);
          console.log('🔍 Campos disponíveis:', Object.keys(dados[0]));
        }
        
        setDados(dados);
        setEstatisticas(response.meta?.estatisticas || null);
        
        // Atualizar informações de paginação
        if (response.meta?.estatisticas?.paginacao) {
          const paginacao = response.meta.estatisticas.paginacao;
          setTotalRegistros(paginacao.total_registros);
          setTemProximaPagina(paginacao.tem_proxima_pagina);
        }
      } else {
        setError(response.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar relatório:', error);
      setError('Erro ao carregar relatório. Tente novamente.');
    } finally {
      setLoading(false);
      console.log('✅ Carregamento finalizado');
    }
  }, [filtros, api, page, rowsPerPage]);

  // Funções de paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Função para imprimir relatório
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Relatório Encapsulação Detalhado</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .period { margin-bottom: 20px; font-size: 14px; color: #666; }
            @media print {
              body { margin: 0; }
              table { page-break-inside: auto; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Relatório Encapsulação Detalhado</h1>
            <p>Todas as requisições com quantidade e tipo de quantidade</p>
            <div class="period">
              <strong>Período:</strong> ${filtros.dataInicio} a ${filtros.dataFim}
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Código Requisição</th>
                <th>Data</th>
                <th>Funcionário</th>
                <th>Setor</th>
                <th>Quantidade</th>
                <th>Tipo Quantidade</th>
              </tr>
            </thead>
            <tbody>
              ${dados.map(item => `
                <tr>
                  <td>${escapeHtml(item.codigo_requisicao || '')}</td>
                  <td>${escapeHtml(item.data_formatada || item.data || '')}</td>
                  <td>${escapeHtml(item.funcionario_nome || '')}</td>
                  <td>${escapeHtml(item.funcionario_setor || item.setor_efetivo || '')}</td>
                  <td>${item.quantidade || ''}</td>
                  <td>${escapeHtml(item.tipo_quantidade || '')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Função para exportar CSV
  const handleExportCSV = () => {
    const csvContent = [
      // Cabeçalho
      ['Código Requisição', 'Data', 'Funcionário', 'Setor', 'Quantidade', 'Tipo Quantidade'],
      // Dados
      ...dados.map(item => [
        item.codigo_requisicao || '',
        item.data_formatada || item.data || '',
        item.funcionario_nome || '',
        item.funcionario_setor || item.setor_efetivo || '',
        item.quantidade || '',
        item.tipo_quantidade || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio_encapsulacao_detalhado_${filtros.dataInicio}_${filtros.dataFim}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Função para exportar PDF
  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    
    // Título
    doc.setFontSize(20);
    doc.text('Relatório Encapsulação Detalhado', 20, 20);
    
    // Subtítulo
    doc.setFontSize(12);
    doc.text('Todas as requisições com quantidade e tipo de quantidade', 20, 30);
    
    // Período
    doc.setFontSize(10);
    doc.text(`Período: ${filtros.dataInicio} a ${filtros.dataFim}`, 20, 40);
    
    // Tabela de requisições
    const tableData = dados.map(item => [
      item.codigo_requisicao || '',
      item.data_formatada || item.data || '',
      item.funcionario_nome || '',
      item.funcionario_setor || item.setor_efetivo || '',
      item.quantidade || '',
      item.tipo_quantidade || ''
    ]);
    
    doc.autoTable({
      head: [['Código Requisição', 'Data', 'Funcionário', 'Setor', 'Quantidade', 'Tipo Quantidade']],
      body: tableData,
      startY: 50,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [245, 245, 245] }
    });
    
    // Salvar PDF
    doc.save(`relatorio_encapsulacao_detalhado_${filtros.dataInicio}_${filtros.dataFim}.pdf`);
  };

  // Carregar dados quando filtros mudarem
  useEffect(() => {
    if (filtros.dataInicio && filtros.dataFim) {
      carregarDados();
    }
  }, [filtros.dataInicio, filtros.dataFim, filtros.funcionario, filtros.tipoQuantidade, filtros.quantidade]);

  // Carregar dados quando página ou tamanho da página mudarem
  useEffect(() => {
    if (filtros.dataInicio && filtros.dataFim) {
      carregarDados();
    }
  }, [page, rowsPerPage]);

  // Carregar listas após inicialização
  useEffect(() => {
    carregarFuncionarios();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <ListIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Relatório Encapsulação Detalhado
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Todas as requisições com quantidade e tipo de quantidade
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={funcionarios}
              getOptionLabel={(funcionario) => funcionario.nome}
              value={funcionarios.find(f => f.codigo === filtros.funcionario) || null}
              onChange={(event, novoFuncionario) => {
                setFiltros(prev => ({ 
                  ...prev, 
                  funcionario: novoFuncionario ? novoFuncionario.codigo : '' 
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Funcionário (Opcional)"
                  placeholder="Selecione um funcionário"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tipo Quantidade (Opcional)</InputLabel>
              <Select
                value={filtros.tipoQuantidade}
                onChange={(e) => setFiltros(prev => ({ ...prev, tipoQuantidade: e.target.value }))}
                label="Tipo Quantidade (Opcional)"
              >
                <MenuItem value="">
                  <em>Todos os tipos</em>
                </MenuItem>
                {tiposQuantidade.map(tipo => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Quantidade (Opcional)"
              type="number"
              value={filtros.quantidade}
              onChange={(e) => setFiltros(prev => ({ ...prev, quantidade: e.target.value }))}
              placeholder="Ex: 30, 60, 120"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={carregarDados}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
              >
                {loading ? 'Carregando...' : 'Gerar Relatório'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={handlePrint}
                disabled={loading || dados.length === 0}
                startIcon={<PrintIcon />}
                color="primary"
              >
                Imprimir
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleExportCSV}
                disabled={loading || dados.length === 0}
                startIcon={<FileDownloadIcon />}
                color="success"
              >
                Exportar CSV
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleExportPDF}
                disabled={loading || dados.length === 0}
                startIcon={<PictureAsPdfIcon />}
                color="error"
              >
                Exportar PDF
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Estatísticas */}
      {estatisticas && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main" fontWeight="bold">
                  {estatisticas.total_leituras || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de Requisições
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {estatisticas.total_capsulas || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de Cápsulas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {estatisticas.funcionarios_unicos || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Funcionários Únicos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {estatisticas.periodo_dias || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dias do Período
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabela de Requisições */}
      {dados.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
            Requisições Detalhadas
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Código Requisição</strong></TableCell>
                  <TableCell><strong>Data</strong></TableCell>
                  <TableCell><strong>Funcionário</strong></TableCell>
                  <TableCell><strong>Setor</strong></TableCell>
                  <TableCell><strong>Quantidade</strong></TableCell>
                  <TableCell><strong>Tipo Quantidade</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.codigo_requisicao || '-'}</TableCell>
                    <TableCell>{item.data_formatada || item.data || '-'}</TableCell>
                    <TableCell>{item.funcionario_nome || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={item.funcionario_setor || item.setor_efetivo || '-'} 
                        size="small" 
                        color="primary" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.quantidade || '-'} 
                        size="small" 
                        color="secondary" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.tipo_quantidade || '-'} 
                        size="small" 
                        color="info" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginação */}
          <TablePagination
            component="div"
            count={totalRegistros}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}

      {/* Empty State */}
      {!loading && dados.length === 0 && !error && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ListIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhuma requisição encontrada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ajuste os filtros e tente novamente
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioEncapsulacaoDetalhado;
